import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useQnAStore } from "@/experiences/qna/store/qna.store";
import { useStudyUsers } from "@/store/study-users.store";
import { useQuestionFormatting } from "@/experiences/qna/composables/question-formatting";
import { useQuestionScope } from "@/experiences/qna/composables/question-scope";
import EventTrackingApi from "@/api/event-tracking";
import { useContext } from "@/store/context";
import { isSameActivity, isSameTask } from "@/experiences/qna/utils/comparison-utils";
import { useStudiesStore } from "@/store/studies";
import { QueryFilterKey, QnAAnswerErrorReasonCode, AnalysisMode } from "@/types/ai";
import { QnAPanelViewTab } from "@/experiences/qna/types";
// Static resources
const isWarningError = (error) => [
    QnAAnswerErrorReasonCode.InsufficientContext,
    QnAAnswerErrorReasonCode.UnsupportedAnswer,
    QnAAnswerErrorReasonCode.CrossStudyNoRelevantStudyAnswers
].includes(error.reasonCode);
const shouldThrowError = (error) => [QnAAnswerErrorReasonCode.EmptySegment, QnAAnswerErrorReasonCode.MentionsNotSupported].includes(error.reasonCode);
// Composable
export const useQnA = () => {
    const { getFiltersFromScope } = useQuestionScope();
    const { questionToHtml } = useQuestionFormatting();
    const qnaStore = useQnAStore();
    const { qnaUIState, erroredFollowUpConversations, allSegmentsMap, studyActivitiesMap, taskMap, followUpConversations, availableStudies, studyDiscussionsMap } = storeToRefs(qnaStore);
    const activeTab = computed(() => QnAPanelViewTab[qnaUIState.value.tab.index]);
    const activeTabIndex = computed(() => qnaUIState.value.tab.index);
    const { users } = storeToRefs(useStudyUsers());
    const { currentStudyId } = storeToRefs(useContext());
    const crossStudyMode = computed(() => availableStudies.value.length > 1 && qnaUIState.value.question.filters.studies.length !== 1);
    const { studiesMap } = storeToRefs(useStudiesStore());
    const currentStudySelected = computed(() => !crossStudyMode.value &&
        qnaUIState.value.question.filters.studies.length === 1 &&
        qnaUIState.value.question.filters.studies[0].studyId === currentStudyId.value);
    const resetContentScroll = (tab) => {
        for (const key in qnaUIState.value.tab.scrollPositions) {
            if (tab === key || key.startsWith(`${tab}_`)) {
                qnaUIState.value.tab.scrollPositions[key] = 0;
            }
        }
        qnaUIState.value.verbatims.scrollPositions = {};
    };
    const toTab = (index) => {
        const tabName = QnAPanelViewTab[index];
        if (tabName === undefined)
            throw new Error(`useQnAPanel > toTab(): Invalid tab index "${index}"`);
        if (tabName !== activeTab.value)
            qnaUIState.value.tab.index = index;
    };
    const askNewQuestion = () => {
        qnaStore.resetQnAUIState({
            sidebar: { ...qnaUIState.value.sidebar }
        });
    };
    const processFilters = (filters) => {
        const getActivityTasks = (studyId, activityId) => {
            // Get the key for this study-activity pair
            const key = qnaStore.getStudyActivityKey(studyId, activityId);
            const tasks = taskMap.value.get(key) ?? [];
            return new Map(tasks.map((task) => [task.taskId, task]));
        };
        return Object.entries(filters).reduce((acc, [key, values]) => {
            switch (key) {
                case QueryFilterKey.Studies:
                    acc[key] = values.map(({ studyId }) => {
                        const study = qnaStore.availableStudies.find((s) => s.studyId === studyId);
                        return {
                            ...study,
                            studyId,
                            unavailable: !study
                        };
                    });
                    break;
                case QueryFilterKey.Activities:
                    acc[key] = values.map((activity) => {
                        const activityId = activity.activityId;
                        const studyId = activity.studyId;
                        const activities = studyActivitiesMap.value.get(studyId) || new Map();
                        const foundActivity = activities.get(activityId);
                        return {
                            ...(foundActivity || {}),
                            activityId,
                            studyId,
                            unavailable: !foundActivity || foundActivity.deleted
                        };
                    });
                    break;
                case QueryFilterKey.Tasks:
                    acc[key] = values.map((task) => {
                        const taskId = task.taskId;
                        const activityId = task.activityId;
                        const studyId = task.studyId;
                        const foundTask = getActivityTasks(studyId, activityId).get(taskId);
                        return {
                            ...(foundTask || {}),
                            taskId,
                            activityId,
                            studyId,
                            unavailable: !foundTask
                        };
                    });
                    break;
                case QueryFilterKey.Segments:
                    acc[key] = values.map(({ segmentId }) => {
                        const segment = allSegmentsMap.value.get(segmentId);
                        return {
                            ...segment,
                            segmentId,
                            unavailable: segment?.status !== "ACTIVE"
                        };
                    });
                    break;
                case QueryFilterKey.Participants:
                    acc[key] = values.map(({ userId }) => {
                        const user = users.value[userId];
                        return {
                            userId,
                            name: user?.displayName,
                            unavailable: !user
                        };
                    });
                    break;
                case QueryFilterKey.Discussions:
                    acc[key] = values.map(({ topicId, studyId, categoryId, parentCategoryId }) => {
                        // Get the discussions data for this study
                        const storedTopic = studyDiscussionsMap.value.get(studyId)?.topics.get(topicId) || { unavailable: true };
                        return {
                            ...storedTopic,
                            topicId,
                            studyId,
                            categoryId,
                            parentCategoryId
                        };
                    });
                    break;
                case QueryFilterKey.Categories:
                    acc[key] = values.reduce((result, { categoryId, studyId, parentCategoryId }) => {
                        if (categoryId !== undefined) {
                            const storedCategory = studyDiscussionsMap.value.get(studyId)?.categories.get(categoryId);
                            result.push(storedCategory
                                ? { ...storedCategory, categoryId, studyId, parentCategoryId }
                                : { unavailable: true, categoryId, studyId, parentCategoryId });
                        }
                        return result;
                    }, []);
                    break;
                default:
            }
            return acc;
        }, {});
    };
    const askQuestionAgain = (response) => {
        EventTrackingApi.logEvent({
            eventName: "AI Questions: Ask again clicked"
        });
        if (!response) {
            resetContentScroll("0");
            qnaUIState.value.tab.index = QnAPanelViewTab.Question;
            return;
        }
        qnaStore.fetchResponse(response.requestId);
        resetContentScroll(QnAPanelViewTab[QnAPanelViewTab.Question]);
        // Prevent errors from a previous question to show when loading historical question
        if (erroredFollowUpConversations.value.has("NEW_QUESTION")) {
            erroredFollowUpConversations.value.delete("NEW_QUESTION");
        }
        const filters = getFiltersFromScope(response.scope);
        qnaUIState.value = {
            ...qnaUIState.value,
            question: {
                text: questionToHtml(response.question, { onlySpans: true }),
                analysisMode: response.analysisMode || AnalysisMode.STANDARD,
                filters,
                mentions: {},
                preserveFormData: true
            },
            tab: {
                ...qnaUIState.value.tab,
                index: 0
            },
            history: {
                ...qnaUIState.value.history,
                page: 0
            },
            verbatims: {
                ...qnaUIState.value.verbatims,
                requestId: response.requestId,
                responseDate: response.requestDate
            },
            followUp: {
                ...qnaUIState.value.followUp,
                rootRequestId: response.requestId
            }
        };
    };
    // Conversation filter chips data
    const filtersFallbackValue = {
        studies: [],
        activities: [],
        tasks: [],
        categories: [],
        discussions: [],
        segments: [],
        participants: []
    };
    const hasUnavailableFiltersInScope = (filters) => Object.values(filters).some((f) => f.some(({ unavailable }) => !!unavailable));
    const hasUnavailableFilters = computed(() => {
        let filters = filtersFallbackValue;
        switch (activeTab.value) {
            case QnAPanelViewTab[QnAPanelViewTab.Question]:
                filters = qnaUIState.value.question.filters;
                break;
            case QnAPanelViewTab[QnAPanelViewTab.FollowUp]:
                if (qnaUIState.value.followUp.rootRequestId) {
                    const scope = followUpConversations.value.get(qnaUIState.value.followUp.rootRequestId)?.[0]?.scope;
                    if (scope)
                        filters = getFiltersFromScope(scope);
                }
                break;
            case QnAPanelViewTab[QnAPanelViewTab.HistoryFollowUp]:
                if (qnaUIState.value.historyFollowUp.rootRequestId) {
                    const scope = followUpConversations.value.get(qnaUIState.value.historyFollowUp.rootRequestId)?.[0]?.scope;
                    if (scope)
                        filters = getFiltersFromScope(scope);
                }
                break;
            default:
        }
        return hasUnavailableFiltersInScope(filters);
    });
    const shouldResetQnAUIState = () => {
        let shouldResetFilters = false;
        const { studies, activities, segments, categories, discussions } = qnaUIState.value.question.filters ?? {};
        const noStudiesSelected = studies.length === 0;
        const noCurrentStudy = currentStudyId.value === undefined;
        const hasActivitiesOrSegmentsSelected = activities.length > 0 || segments.length > 0;
        const hasCategoriesOrDiscussionsSelected = (categories?.length ?? 0) > 0 || (discussions?.length ?? 0) > 0;
        const hasMultipleStudiesSelected = studies.length > 1;
        // Check if single selected study differs from current study (navigation between studies)
        const singleStudyDiffersFromCurrent = studies.length === 1 && !currentStudySelected.value;
        if (hasActivitiesOrSegmentsSelected || hasMultipleStudiesSelected || hasCategoriesOrDiscussionsSelected) {
            shouldResetFilters = false;
        }
        else if (noCurrentStudy || noStudiesSelected || singleStudyDiffersFromCurrent) {
            shouldResetFilters = true;
        }
        if (shouldResetFilters) {
            const currStudy = currentStudyId.value && studiesMap.value.get(currentStudyId.value); // Will resolve to `false` when there is no current study
            qnaUIState.value.question.filters = {
                studies: currStudy ? [currStudy] : [],
                activities: [],
                tasks: [],
                categories: [],
                discussions: [],
                participants: [],
                segments: []
            };
            const currentStudyIds = new Set(qnaUIState.value.question.filters.studies.map((study) => study.studyId));
            const previousStudyIds = new Set(qnaUIState.value.studyIds);
            const studyFiltersUnchanged = currentStudyIds.size === previousStudyIds.size && [...currentStudyIds].every((id) => previousStudyIds.has(id));
            if (!studyFiltersUnchanged) {
                qnaUIState.value.question.text = "";
            }
        }
        return shouldResetFilters;
    };
    const toggleStudyFilter = (study) => {
        if (!study || !study.studyId)
            return; // Add null check
        const filters = qnaUIState.value.question.filters;
        const index = filters.studies.findIndex((s) => s.studyId === study.studyId);
        if (index > -1) {
            filters.studies.splice(index, 1);
        }
        else {
            filters.studies.push(study);
        }
    };
    const toggleActivityFilter = (activity) => {
        const filters = qnaUIState.value.question.filters;
        const index = filters.activities.findIndex((a) => isSameActivity(a, activity));
        if (index > -1) {
            filters.activities.splice(index, 1);
        }
        else {
            filters.activities.push(activity);
        }
    };
    const toggleTaskFilter = (task) => {
        const filters = qnaUIState.value.question.filters;
        const index = filters.tasks.findIndex((t) => isSameTask(t, task));
        if (index > -1) {
            filters.tasks.splice(index, 1);
        }
        else {
            filters.tasks.push(task);
        }
    };
    const isStudySelected = (studyId) => {
        return qnaUIState.value.question.filters.studies.some((s) => s.studyId === studyId);
    };
    return {
        activeTab,
        activeTabIndex,
        hasUnavailableFilters,
        crossStudyMode,
        currentStudySelected,
        toTab,
        askQuestionAgain,
        askNewQuestion,
        resetContentScroll,
        processFilters,
        isWarningError,
        toggleStudyFilter,
        toggleActivityFilter,
        toggleTaskFilter,
        isStudySelected,
        isSameTask,
        isSameActivity,
        shouldThrowError,
        shouldResetQnAUIState
    };
};
